import type { CSSProperties, ReactNode, SyntheticEvent } from 'react';
import { useRef } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { usePostHog } from 'posthog-js/react';

import { usePageInformationContext } from '@/context/PageInformationContext';

import { TooltipIconButton } from '../TooltipIconButton';

interface Tab {
  name: string;
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  closable?: boolean;
  onClose?: (value: number) => void;
  tag?: ReactNode;
  sortableHandleRef?: React.RefObject<HTMLElement | null>;
}

interface CustomTabsProps {
  tabs: Tab[];
  activeTab: number;
  onChange?: (index: number) => void;
  onAddTab?: () => void;
  height?: number;
  minTabWidth?: number;
  offset?: number;
  maxTabWidth?: number | 'unset';
  scrollButtons?: boolean | 'auto';
  disableRipple?: boolean;
}

type MuiTabsInjectedTabProps = {
  fullWidth?: boolean;
  indicator?: ReactNode;
  onChange?: (event: SyntheticEvent, value: unknown) => void;
  selected?: boolean;
  selectionFollowsFocus?: boolean;
  textColor?: 'inherit' | 'primary' | 'secondary';
  value?: unknown;
};

interface SortableTabItemProps extends MuiTabsInjectedTabProps {
  id: string;
  index: number;
  handle?: React.RefObject<HTMLElement | null>;
  container: React.RefObject<HTMLDivElement | null>;
  label: ReactNode;
  height: number;
  minTabWidth: number;
  maxTabWidth: number | 'unset';
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  disabled?: boolean;
  ariaControls: string;
  tabId: string;
  name: string;
  onSelectTab?: (index: number) => void;
  activeTab: number;
  onClick: () => void;
}

const tabsRootSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0
};

const tabsSx: SxProps<Theme> = {
  minWidth: 0,
  maxWidth: 'calc(100% - var(--tabs-offset))',
  minHeight: 'var(--tabs-height)',
  height: 'var(--tabs-height)',
  '& .MuiTabs-scrollButtons.Mui-disabled': {
    display: 'none'
  },
  '.MuiTabScrollButton-root': {
    width: 32,
    minWidth: 32,
    backgroundColor: 'gx.tileLight',
    color: 'gx.textDisabled',
    '&:hover': {
      color: 'white',
      backgroundColor: '#161b40'
    }
  }
};

const tabSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  textTransform: 'initial',
  height: 'var(--tab-height)',
  minWidth: 'var(--tab-min-width)',
  maxWidth: 'var(--tab-max-width)',
  minHeight: 'var(--tab-height)',
  p: 1,
  '&:not(:last-child)': { mr: '2px' }
};

const tabLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
  height: '100%',
  textAlign: 'center'
};

const clickableTabLabelSx: SxProps<Theme> = {
  ...tabLabelSx,
  cursor: 'pointer',
  borderRadius: '4px'
};

const fullWidthLabelSx: SxProps<Theme> = { flexBasis: '100%' };

const closeLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
};

const closeButtonSx: SxProps<Theme> = {
  height: 46,
  position: 'absolute',
  right: 0,
  backgroundColor: 'transparent',
  ':hover': {
    backgroundColor: 'action.hover'
  }
};

const addButtonSx: SxProps<Theme> = {
  width: 32,
  minWidth: 32,
  backgroundColor: 'gx.tileLight'
};

const addIconSx: SxProps<Theme> = {
  fontSize: '14px !important',
  color: 'gx.textDisabled'
};

const SortableTabItem = ({
  id,
  index,
  handle,
  container,
  label,
  height,
  minTabWidth,
  maxTabWidth,
  disableFocusRipple,
  disableRipple,
  disabled,
  ariaControls,
  tabId,
  name,
  onSelectTab,
  activeTab,
  onClick,
  ...muiTabProps
}: SortableTabItemProps) => {
  const internalMuiTabProps = muiTabProps as React.ComponentProps<typeof Tab>;

  const { ref } = useSortable({
    id,
    index,
    handle,
    type: 'dashboard-tab',
    accept: ['dashboard-tab'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  return (
    <Tab
      ref={ref}
      {...internalMuiTabProps}
      aria-controls={ariaControls}
      disabled={disabled}
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      id={tabId}
      label={
        <Box
          role='presentation'
          sx={tabLabelSx}
          onMouseDownCapture={event => {
            if (onSelectTab && activeTab !== index) {
              event.stopPropagation();
              onSelectTab(index);
            }
          }}
        >
          <Box sx={fullWidthLabelSx}>{label}</Box>
        </Box>
      }
      style={
        {
          '--tab-height': `${height}px`,
          '--tab-min-width': `${minTabWidth}px`,
          '--tab-max-width': maxTabWidth === 'unset' ? 'unset' : `${maxTabWidth}px`
        } as CSSProperties
      }
      sx={tabSx}
      onClick={onClick}
    />
  );
};

const CloseButton = ({
  label,
  name,
  onClose,
  index
}: {
  label: ReactNode | undefined;
  index: number;
  name: string;
  onClose?: (value: number) => void;
}) => {
  return (
    <>
      <Box sx={closeLabelSx}>{label || name}</Box>
      <TooltipIconButton
        component='span'
        sx={closeButtonSx}
        title='Remove'
        onClick={e => {
          e.stopPropagation();
          if (onClose) onClose(index);
        }}
      >
        <ClearOutlined />
      </TooltipIconButton>
    </>
  );
};

const CustomTabs = ({
  tabs,
  activeTab,
  onChange,
  onAddTab,
  height = 48,
  minTabWidth = 180,
  maxTabWidth = 'unset',
  offset = 0,
  scrollButtons = false,
  disableRipple = false,
  ...tabProps
}: CustomTabsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    if (onChange) onChange(newValue);
  };

  const { mainPageTitle, subPageTitle } = usePageInformationContext();

  const postHog = usePostHog();
  const tabsStyle = {
    '--tabs-height': `${height}px`,
    '--tabs-offset': `${offset}px`
  } as CSSProperties;
  const tabStyle = {
    '--tab-height': `${height}px`,
    '--tab-min-width': `${minTabWidth}px`,
    '--tab-max-width': maxTabWidth === 'unset' ? 'unset' : `${maxTabWidth}px`
  } as CSSProperties;

  const handleTabClick = (
    mainPage: string | undefined,
    subPage: string | undefined,
    tabName: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- postHog can be null when not configured
    postHog?.capture('tab_click', {
      mainPage,
      subPage,
      tabName
    });
  };

  return (
    <Box sx={tabsRootSx}>
      <MuiTabs
        ref={containerRef}
        aria-label='tabs'
        scrollButtons={scrollButtons}
        style={tabsStyle}
        sx={tabsSx}
        value={activeTab}
        variant='scrollable'
        onChange={handleChange}
      >
        {tabs.map(
          ({ name, id, label, disabled, closable, onClose, tag, sortableHandleRef }, index) => {
            const tabLabel = closable ? (
              <CloseButton
                index={index}
                label={label}
                name={name}
                onClose={onClose}
              />
            ) : (
              <Box sx={fullWidthLabelSx}>{label || name}</Box>
            );

            if (id && sortableHandleRef) {
              return (
                <SortableTabItem
                  key={id}
                  activeTab={activeTab}
                  ariaControls={`tabpanel-${name}`}
                  container={containerRef}
                  disableFocusRipple={disableRipple}
                  disableRipple={disableRipple}
                  disabled={disabled}
                  handle={sortableHandleRef}
                  height={height}
                  id={id}
                  index={index}
                  label={
                    <>
                      {tabLabel}
                      {tag}
                    </>
                  }
                  maxTabWidth={maxTabWidth}
                  minTabWidth={minTabWidth}
                  name={name}
                  tabId={`tab-${name}`}
                  onClick={() => {
                    handleTabClick(mainPageTitle, subPageTitle, name);
                  }}
                  onSelectTab={onChange}
                />
              );
            }

            return (
              <Tab
                key={id ?? `tab-${name}-${index}`}
                aria-controls={`tabpanel-${name}`}
                disabled={disabled}
                disableFocusRipple={disableRipple}
                disableRipple={disableRipple}
                id={`tab-${name}`}
                label={
                  <Box
                    role='presentation'
                    sx={clickableTabLabelSx}
                    onMouseDownCapture={event => {
                      if (onChange && activeTab !== index) {
                        event.stopPropagation();
                        onChange(index);
                      }
                    }}
                  >
                    {tabLabel}
                    {tag}
                  </Box>
                }
                style={tabStyle}
                sx={tabSx}
                onClick={() => {
                  handleTabClick(mainPageTitle, subPageTitle, name);
                }}
                {...tabProps}
              />
            );
          }
        )}
      </MuiTabs>
      {onAddTab && (
        <TooltipIconButton
          sx={addButtonSx}
          title='Add new page'
          onClick={onAddTab}
        >
          <AddOutlined sx={addIconSx} />
        </TooltipIconButton>
      )}
    </Box>
  );
};

export type { Tab };
export default CustomTabs;
